import { TFunction } from 'i18next'

import { Transaction } from '@/domain'
import { transactionServices } from '@/domain'

export interface FormattedTransactionItem {
  uri: string
  name: string
  backgroundColor: string | [string, string, ...string[]]
  title: string
  subTitle?: string
  headerTitle: string
  secondaryValue: string
  iconName?: string
  iconSize?: number
  isSentToNonUser?: boolean
}

export interface FormattedTransactionItemProps {
  item: Transaction & {
    currencyIconUrl?: string
    currencyColorReference?: string | [string, string, ...string[]]
    currencyName?: string
  }
  t: TFunction<'translation', undefined>
}

export const formatTransactionItem = (
  props: FormattedTransactionItemProps,
): FormattedTransactionItem => {
  const { item, t } = props

  // Get transaction conditions first
  const isConvert = item.type === 'swap_convert'
  const isCardWriteOff = item.type === 'card_write_off'

  // Base object to avoid repetition
  let baseDetails: FormattedTransactionItem = {
    uri: item.currencyIconUrl || '',
    name: item.currencyName || '',
    backgroundColor: item.currencyColorReference || '',
    title: '',
    subTitle: '',
    headerTitle: '',
    secondaryValue: '',
  }

  // For card_write_off transactions, use card icon with purple background (like TransactionItem)
  if (isCardWriteOff) {
    baseDetails = {
      ...baseDetails,
      uri: '', // No image, use icon instead
      iconName: 'card',
      backgroundColor: '#8B5CF6',
    }
  }

  // For conversions, use target currency data like in RN
  if (isConvert && item.subject_swap?.credit_amount) {
    const targetCurrency = item.subject_swap.credit_amount.currency
    baseDetails = {
      ...baseDetails,
      // Keep existing currencyIconUrl and currencyColorReference from LatestActivities
      uri: item.currencyIconUrl || '',
      backgroundColor: item.currencyColorReference || '',
      name: targetCurrency, // Fallback for currency icon
    }
  }

  // Get transaction conditions
  const isSend = item.direction === 'send'
  const isKemToKem = item.type === 'kem_to_kem'

  // For kem_to_kem transactions, use user data like in RN
  if (isKemToKem && item.subject_user) {
    baseDetails = {
      ...baseDetails,
      uri: item.subject_user.avatar_path || '',
      name:
        item.subject_user.first_name ||
        item.subject_user.phone_number ||
        'Unknown',
      backgroundColor: !item.subject_user.username
        ? ['#6366F1', '#8B5CF6']
        : '', // Gradient for non-verified users
    }
  }
  // Format amounts
  const { amount, currency } = transactionServices.formatTransactionAmount(item)
  const formattedAmount = `${amount} ${currency}`

  // Generate title using transactionServices (now properly translated)
  const title = transactionServices.getTransactionTitle(item, t)

  // Generate subtitle
  let subTitle = ''
  // Don't show subtitle for kem_to_kem transactions
  // if (isKemToKem && item.subject_user) {
  //   subTitle = isSend
  //     ? `To ${item.subject_user.first_name || item.subject_user.phone_number || 'Unknown'}`
  //     : `From ${item.subject_user.first_name || item.subject_user.phone_number || 'Unknown'}`
  // }

  // Generate secondary value (for conversions, etc.)
  let secondaryValue = ''
  if (isConvert && item.subject_swap?.credit_amount) {
    // For conversions, show the amount plus fee like in RN
    secondaryValue = formattedAmount
  } else if (isKemToKem) {
    // For kem_to_kem, show fiat amount like in RN (use amount.fiat_amount if available)
    secondaryValue = item.amount?.fiat_amount
      ? `≈$${item.amount.fiat_amount}`
      : formattedAmount
  }

  // Determine if sent to non-user (like in RN)
  const isSentToNonUser = isKemToKem && isSend && !item.subject_user?.username

  return {
    ...baseDetails,
    title,
    subTitle,
    headerTitle: title,
    secondaryValue,
    isSentToNonUser,
  }
}
